import apiClient from "./apiClient";

// ─── DTOs (mirror backend records) ───────────────────────────────────────────

export interface GeneralStats {
  totalEntries: number;
  // Per-domain entity totals paired with the total number of entries (tanım) written
  // in that domain. Order mirrors the statistics page rows: custom topic (gündem),
  // match, club, person, team. The five entry counts sum to totalEntries.
  totalCustomTopics: number;
  totalCustomTopicEntries: number;
  totalMatches: number;
  totalMatchEntries: number;
  totalClubs: number;
  totalClubEntries: number;
  totalPeople: number;
  totalPersonEntries: number;
  totalTeams: number;
  totalTeamEntries: number;
  totalActiveAuthors: number;
  totalActiveNewbies: number;
  totalUpvotes: number;
  avgEntriesPerTopic: number;
  avgEntriesPerAuthor: number;
  // Soft-deleted entry counts — same per-domain breakdown as the active counts
  // above, but for entries with deletedAt set. The five per-domain counts sum
  // to totalDeletedEntries, same relationship as the active counts above.
  totalDeletedEntries: number;
  totalCustomTopicEntriesDeleted: number;
  totalMatchEntriesDeleted: number;
  totalClubEntriesDeleted: number;
  totalPersonEntriesDeleted: number;
  totalTeamEntriesDeleted: number;
  // Average character count of a single active (non-deleted) entry, across all five
  // domains (custom topic, match, club, person, team).
  avgEntryCharCount: number;
}

export interface DistributionItem {
  label: string;
  count: number;
}

export interface AuthorOverview {
  genderDistribution: DistributionItem[];
  ageDistribution: DistributionItem[];
  countryDistribution: DistributionItem[];
  domesticCityDistribution: DistributionItem[];
  abroadCityDistribution: DistributionItem[];
}

export interface TopAuthor {
  userId: number;
  username: string;
  entryCount: number;
}

/**
 * User ranked by karma (upVotes - downVotes).
 * Returned by GET /api/statistics/users/top-karma.
 * karma can be negative for users with more down votes than up votes.
 */
export interface TopKarmaUser {
  userId: number;
  username: string;
  karma: number;
  entryCount: number;
}

export interface TopEntry {
  entryId: number;
  authorUserId: number | null;
  authorUsername: string;
  contentPreview: string;
  // Match reference fields (null for non-match entries)
  matchId: number | null;
  matchSlug: string | null;
  homeTeamName: string | null;
  awayTeamName: string | null;
  matchDate: string | null;
  // Person reference fields (null for non-person entries)
  personId: number | null;
  personSlug: string | null;
  personName: string | null;
  // Club reference fields (null for non-club entries)
  clubId: number | null;
  clubSlug: string | null;
  clubName: string | null;
  // Team reference fields (null for non-team entries)
  teamId: number | null;
  // Parent club slug — used as first path segment in /takim/{clubSlug}/{teamSlug}
  teamClubSlug: string | null;
  // Full team URL slug: {sportSlug}[-gender][-teamCode]
  teamSlug: string | null;
  teamName: string | null;
  upvoteCount: number;
  favoriteCount: number;
  createdAt: string | null;
  // Custom topic (gündem/kanal) reference fields (null for non-custom-topic entries)
  customTopicId: number | null;
  customTopicSlug: string | null;
  customTopicTitle: string | null;
}

// Person ranked by number of entries they received
export interface TopPerson {
  personId: number;
  slug: string;
  displayName: string;
  entryCount: number;
}

// Club ranked by number of entries it received
export interface TopClub {
  clubId: number;
  slug: string;
  shortName: string;
  nameTr: string | null;
  logoUrl: string | null;
  color1: string | null;
  entryCount: number;
}

// Team ranked by number of entries it received
export interface TopTeam {
  teamId: number;
  clubSlug: string;
  displayName: string;
  logoUrl: string | null;
  sportType: string | null;
  entryCount: number;
}

// ─── Period + sort types ──────────────────────────────────────────────────────

export type StatPeriod = "today" | "week" | "month" | "year" | "all";
export type EntrySort  = "votes" | "favorites" | "count" | "karma";

// One row in the fan club ranking
export interface FanClubStatItem {
  clubId:    number;
  shortName: string;
  nameTr:    string | null;
  nameEn:    string | null;
  logoUrl:   string | null;
  color1:    string | null;
  color2:    string | null;
  color3:    string | null;
  count:     number;
}

// The authenticated user's personal ranking snapshot
export interface UserRanking {
  entryCount: number;
  entryRank:  number;
  karma:      number;
  karmaRank:  number;
  // Total user count — denominator for both rank displays (all users appear in both leaderboards)
  totalUsers: number;
}

// ─── Service functions ────────────────────────────────────────────────────────

export const statisticsService = {
  getGeneralStats: (): Promise<GeneralStats> =>
    apiClient.get("/api/statistics/general"),

  /** Fetches the calling user's entry count, karma and their global rank for each. */
  getUserRanking: (): Promise<UserRanking> =>
    apiClient.get("/api/statistics/my-ranking"),

  getAuthorOverview: (role?: "AUTHOR" | "ROOKIE"): Promise<AuthorOverview> => {
    const params = new URLSearchParams();
    if (role) params.set("role", role);
    const qs = params.toString();
    return apiClient.get(`/api/statistics/authors/overview${qs ? "?" + qs : ""}`);
  },

  getTopAuthors: (period: StatPeriod, role?: "AUTHOR" | "ROOKIE"): Promise<TopAuthor[]> => {
    const params = new URLSearchParams({ period });
    if (role) params.set("role", role);
    return apiClient.get(`/api/statistics/authors/top?${params}`);
  },

  /**
   * Top 100 users (all roles) ranked by total entry count across ALL topic types
   * (match + person + club + team entries combined).
   */
  getTopUsersByEntryCount: (period: StatPeriod): Promise<TopAuthor[]> => {
    const params = new URLSearchParams({ period });
    return apiClient.get(`/api/statistics/users/top-entries?${params}`);
  },

  /**
   * Top 100 users (all roles) ranked by karma (upVotes - downVotes)
   * received on their entries within the given period.
   */
  getTopUsersByKarma: (period: StatPeriod): Promise<TopKarmaUser[]> => {
    const params = new URLSearchParams({ period });
    return apiClient.get(`/api/statistics/users/top-karma?${params}`);
  },

  getTopEntries: (period: StatPeriod, sort: EntrySort): Promise<TopEntry[]> => {
    const params = new URLSearchParams({ period, sort });
    return apiClient.get(`/api/statistics/entries/top?${params}`);
  },

  getFanStats: (order: number, role?: "AUTHOR" | "ROOKIE"): Promise<FanClubStatItem[]> => {
    const params = new URLSearchParams({ order: String(order) });
    if (role) params.set("role", role);
    return apiClient.get(`/api/statistics/fans?${params}`);
  },

  getTopPeople: (period: StatPeriod): Promise<TopPerson[]> => {
    const params = new URLSearchParams({ period });
    return apiClient.get(`/api/statistics/people/top?${params}`);
  },

  getTopClubs: (period: StatPeriod): Promise<TopClub[]> => {
    const params = new URLSearchParams({ period });
    return apiClient.get(`/api/statistics/clubs/top?${params}`);
  },

  getTopTeams: (period: StatPeriod): Promise<TopTeam[]> => {
    const params = new URLSearchParams({ period });
    return apiClient.get(`/api/statistics/teams/top?${params}`);
  },
};